import type { OrderDashboardIssue } from '@/api/hooks/useOrderDashboard'
import { LinkButton } from '@/components/Display/LinkButton'
import { formatCount } from '@/utils/stringFormatters'
import { ArrowRightOutlined } from '@ant-design/icons'
import {
  Horizontal,
  NothingMessage,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { Segmented, Skeleton } from 'antd'
import { useMemo, useState } from 'react'
import { IssueCard } from './IssueCard'
import type { IssueSortOrder } from './issueCounts'
import { CONNECTION_LOGS_PATH } from './issueFields'
import { type IssueGroup, groupIssues, sortIssueGroups } from './issueGroups'

interface IssueListProps {
  issues: OrderDashboardIssue[]
  loading: boolean
  tenantId: string | null
  tenantName: string | null
  /** Key of the issue group open in the details panel, if any. */
  selectedKey: string | null
  /** Opens an issue in the section's details panel. */
  onViewIssue: (group: IssueGroup) => void
  /** Re-reads the issues once a retry has come back. */
  onRetried: () => void
}

/**
 * The scrollable feed under the count cards, one card per unique issue rather
 * than per failed order. Grouping and sorting are local -- the whole window is
 * already in hand, so there is nothing to ask the backend for.
 */
export function IssueList({
  issues,
  loading,
  tenantId,
  tenantName,
  selectedKey,
  onViewIssue,
  onRetried,
}: IssueListProps) {
  const [order, setOrder] = useState<IssueSortOrder>('newest')

  const sorted = useMemo(
    () => sortIssueGroups(groupIssues(issues), order),
    [issues, order],
  )

  return (
    <Vertical gap={8} style={{ flex: 1, minHeight: 0 }}>
      <Horizontal justifyContent='space-between' alignItems='center' gap={8}>
        <Horizontal gap={6} alignItems='baseline'>
          <Texto weight={600}>Open Issues</Texto>
          <Texto appearance='hint'>{`(${formatCount(sorted.length)})`}</Texto>
        </Horizontal>
        <Horizontal gap={12} verticalCenter>
          <Segmented<IssueSortOrder>
            size='small'
            value={order}
            onChange={setOrder}
            options={[
              { value: 'newest', label: 'Newest' },
              { value: 'oldest', label: 'Oldest' },
            ]}
          />
          {/* A button rather than a bare link, so it sits on the same
              baseline as the sort control and reads like the View Order Logs
              button on the cards below. */}
          <LinkButton size='small' type='link' to={CONNECTION_LOGS_PATH}>
            View All Logs <ArrowRightOutlined style={{ fontSize: 10 }} />
          </LinkButton>
        </Horizontal>
      </Horizontal>

      <Vertical scroll gap={8} style={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : sorted.length === 0 ? (
          <NothingMessage
            title='Nothing to resolve'
            message='No issues match these filters in this window.'
          />
        ) : (
          sorted.map((group) => (
            <IssueCard
              key={group.key}
              group={group}
              selected={group.key === selectedKey}
              tenantId={tenantId}
              tenantName={tenantName}
              onViewIssue={onViewIssue}
              onRetried={onRetried}
            />
          ))
        )}
      </Vertical>
    </Vertical>
  )
}
